import React, { useState, useEffect } from 'react';
import { ProgressBar } from 'react-bootstrap';

/**
 * Funciones auxiliares para cálculos de tiempo
 */
export const formatElapsedTime = (startTime) => {
    if (!startTime) return "0s";
    
    const elapsed = Date.now() - startTime;
    if (elapsed < 60000) return `${Math.round(elapsed/1000)}s`;
    const minutes = Math.floor(elapsed/60000);
    const seconds = Math.round((elapsed%60000)/1000);
    return `${minutes}m ${seconds}s`;
};

export const calculateETA = (currentProgress, startTime) => {
    if (!startTime || currentProgress <= 0) return "Calculando...";
    
    const elapsed = Date.now() - startTime;
    const rate = currentProgress / elapsed;
    const remaining = (100 - currentProgress) / rate;
    
    if (remaining < 60000) return `${Math.round(remaining/1000)}s restantes`;
    const minutes = Math.floor(remaining/60000);
    return `${minutes}m restantes`;
};

export const getCurrentProcessingComponent = (loadingComponents) => {
    const processing = Object.entries(loadingComponents)
        .find(([_, state]) => state.loading && !state.complete);
    
    if (!processing) return "Finalizando...";
    
    const componentNames = {
        table: "Tabla de datos",
        vehicles: "Gráfico de vehículos", 
        alerts: "Gráfico de alertas",
        map: "Mapa",
        frequency: "Gráfico de frecuencia"
    };
    
    return componentNames[processing[0]] || processing[0];
};

/**
 * Componente de barra de progreso con métricas en tiempo real
 */
const ProgressBarWithRealTimeMetrics = ({ 
    loadingComponents, 
    calculateTotalProgress, 
    debugMetrics,
    enhancedMode = false
}) => {
    const [animatedProgress, setAnimatedProgress] = useState(0);
    
    // ✅ FUNCIÓN PARA CALCULAR PROGRESO DE DESCARGA PONDERADO
    // const calculateDownloadProgress = () => {
    //     if (!debugMetrics?.connections) {
    //         return 0;
    //     }

    //     // ✅ IDENTIFICAR CONEXIONES ACTIVAS (downloading o completed)
    //     const activeConnections = Object.entries(debugMetrics.connections).filter(([id, conn]) => 
    //         conn.status === 'downloading' || conn.status === 'completed'
    //     );

    //     console.log('📡 [ProgressBarWithRealTimeMetrics] Calculando progreso:', {
    //         timestamp: new Date().toLocaleTimeString(),
    //         total_conexiones: Object.keys(debugMetrics.connections).length,
    //         conexiones_activas: activeConnections.length,
    //         conexiones_detalle: activeConnections.map(([id, conn]) => ({
    //             id,
    //             name: conn.name,
    //             status: conn.status,
    //             downloadProgress: conn.downloadProgress || 0
    //         }))
    //     });

    //     if (activeConnections.length === 0) return 0;

    //     // ✅ CALCULAR PESO DINÁMICO BASADO EN CONEXIONES ACTIVAS
    //     const weightPerConnection = 100 / activeConnections.length;
    //     let totalWeightedProgress = 0;

    //     activeConnections.forEach(([id, conn]) => {
    //         // ✅ OBTENER PROGRESO DE LA CONEXIÓN
    //         let connectionProgress = 0;
            
    //         if (conn.status === 'completed') {
    //             connectionProgress = 100;
    //         } else if (conn.status === 'downloading') {
    //             connectionProgress = conn.downloadProgress || 0;
    //         }

    //         // ✅ CALCULAR PROGRESO PONDERADO
    //         const weightedProgress = (connectionProgress * weightPerConnection) / 100;
    //         totalWeightedProgress += weightedProgress;

    //         console.log(`📊 [ProgressBarWithRealTimeMetrics] Conexión ${id}:`, {
    //             name: conn.name,
    //             status: conn.status,
    //             rawProgress: connectionProgress,
    //             weight: weightPerConnection.toFixed(1),
    //             weightedProgress: weightedProgress.toFixed(1)
    //         });
    //     });

    //     const finalProgress = Math.min(totalWeightedProgress, 100);
        
    //     console.log('📈 [ProgressBarWithRealTimeMetrics] Resultado final:', {
    //         conexiones_activas: activeConnections.length,
    //         peso_por_conexion: weightPerConnection.toFixed(1),
    //         progreso_total_ponderado: totalWeightedProgress.toFixed(1),
    //         progreso_final: finalProgress.toFixed(1)
    //     });

    //     return finalProgress;
    // };

    const calculateDownloadProgress = () => {
        if (!debugMetrics?.connections) {
                // ✅ FALLBACK mejorado para cuando no hay debugMetrics
                if (enhancedMode && calculateTotalProgress) {
                    return calculateTotalProgress();
                }
                return 0;
        }

    // ✅ FILTRAR solo conexiones con progreso real
    // const activeConnections = Object.entries(debugMetrics.connections).filter(([id, conn]) => 
    //     conn.status === 'downloading' && conn.downloadProgress !== undefined
    // );
    const activeConnections = Object.entries(debugMetrics.connections).filter(([id, conn]) => 
            conn.status === 'downloading' || conn.status === 'active' || conn.status === 'completed'
        );

      if (activeConnections.length === 0) {
            // ✅ Si no hay conexiones activas pero hay procesamiento, usar calculateTotalProgress
            if (enhancedMode && calculateTotalProgress) {
                return calculateTotalProgress();
            }
            return 0;
        }

    // ✅ INCLUIR conexiones completadas
    const completedConnections = Object.entries(debugMetrics.connections).filter(([id, conn]) => 
        conn.status === 'completed'
    );

    const allRelevantConnections = [...activeConnections, ...completedConnections];

    if (allRelevantConnections.length === 0) return 0;

    // ✅ CALCULAR PROGRESO PONDERADO MEJORADO
    const weightPerConnection = 100 / allRelevantConnections.length;
    let totalWeightedProgress = 0;

    allRelevantConnections.forEach(([id, conn]) => {
        let connectionProgress = 0;
        
        if (conn.status === 'completed') {
            connectionProgress = 100;
        } else if (conn.status === 'downloading') {
            connectionProgress = Math.max(0, Math.min(100, conn.downloadProgress || 0));
        }

        const weightedProgress = (connectionProgress * weightPerConnection) / 100;
        totalWeightedProgress += weightedProgress;
    });

    return Math.min(totalWeightedProgress, 100);
};


    // ✅ DETECTAR REINICIO
    const isInitializing = !debugMetrics.searchStartTime && debugMetrics.globalTotalRecordsExpected === 0;
    
    // ✅ USAR PROGRESO DE DESCARGA PERSONALIZADO
    const downloadProgress = calculateDownloadProgress();
    
    useEffect(() => {
        const currentProgress = calculateTotalProgress();
        if (Math.abs(currentProgress - animatedProgress) > 0.1) {
            setAnimatedProgress(currentProgress);
        }
    }, [calculateTotalProgress, animatedProgress]);

    // ✅ CALCULAR MÉTRICAS RELEVANTES
    const totalRecordsReceived = debugMetrics.globalTotalRecordsReceived || 0;
    const totalRecordsExpected = debugMetrics.globalTotalRecordsExpected || 0;
    const activeConnections = debugMetrics.activeConnections || 0;
    
    // ✅ DETERMINAR ESTADO PRINCIPAL USANDO NUESTRO PROGRESO PERSONALIZADO
    const isDownloading = activeConnections > 0 && downloadProgress < 100;
    const isProcessing = !isDownloading && animatedProgress > 0 && animatedProgress < 100;
    const isCompleted = downloadProgress >= 100 && animatedProgress >= 100;

    // ✅ DETERMINAR MENSAJE PRINCIPAL SIMPLE
    const getMainStatus = () => {
        if (isInitializing) {
            return {
                message: "Preparando reporte...",
                icon: "fas fa-sync-alt fa-spin",
                color: "text-muted"
            };
        }
        
        if (isDownloading) {
            const activeConnectionsCount = debugMetrics?.connections ? 
                Object.values(debugMetrics.connections).filter(conn => 
                    conn.status === 'downloading' || conn.status === 'completed'
                ).length : 0;
            
            return {
                message: `Descargando datos... ${downloadProgress.toFixed(0)}%${activeConnectionsCount > 1 ? ` (${activeConnectionsCount} fuentes)` : ''}`,
                icon: "fas fa-download",
                color: "text-primary"
            };
        }
        
        if (isProcessing) {
            return {
                message: `Procesando gráficos... ${animatedProgress.toFixed(0)}%`,
                icon: "fas fa-chart-bar",
                color: "text-info"
            };
        }
        
        if (isCompleted) {
            return {
                message: "Reporte completado",
                icon: "fas fa-check-circle",
                color: "text-success"
            };
        }
        
        return {
            message: "Generando reporte...",
            icon: "fas fa-cog fa-spin",
            color: "text-primary"
        };
    };

    const status = getMainStatus();
    const totalProgress = isDownloading ? downloadProgress : animatedProgress;

    const isDownloadPhase = debugMetrics?.connections && 
        Object.values(debugMetrics.connections).some(conn => 
            conn.status === 'downloading' || conn.status === 'active'
        );

    const progress = calculateDownloadProgress();
    const variant = isDownloadPhase ? 'success' : 'primary';
    const description = isDownloadPhase ? 'Descargando datos...' : 'Procesando datos...';
    const icon = isDownloadPhase ? 'fa-download' : 'fa-cogs';

    
    return (
        <div className="progress-container mb-3">
            {/* ✅ ENCABEZADO SIMPLIFICADO */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                    <i className={`${status.icon} me-2 ${status.color}`}></i>
                    <span className={`fw-bold ${status.color}`}>{status.message}</span>
                </div>
                <div className="text-end">
                    {/* ✅ TIEMPO TRANSCURRIDO SIMPLE */}
                    {debugMetrics.searchStartTime && !isInitializing && (
                        <div className="small text-muted">
                            {formatElapsedTime(debugMetrics.searchStartTime)}
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ BARRA DE PROGRESO PRINCIPAL */}
            <ProgressBar 
                style={{ height: 20 }} 
                className="shadow-sm rounded-3"
                animated={totalProgress < 100 && !isInitializing}
            >
                <ProgressBar
                    now={isInitializing ? 0 : totalProgress}
                    variant={
                        isInitializing ? "secondary" :
                        isDownloading ? "primary" :
                        isProcessing ? "info" :
                        isCompleted ? "success" : "primary"
                    }
                    style={{ 
                        transition: 'width 0.5s ease-in-out',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                    label={isInitializing ? "" : `${totalProgress.toFixed(0)}%`}
                />
            </ProgressBar>

            {/* ✅ INFORMACIÓN ADICIONAL DE CONEXIONES DURANTE DESCARGA */}
            {!isInitializing && isDownloading && debugMetrics?.connections && (
                <div className="mt-2 p-2 bg-light rounded-2">
                    <div className="row small">
                        {Object.entries(debugMetrics.connections)
                            .filter(([id, conn]) => conn.status === 'downloading' || conn.status === 'completed')
                            .map(([id, conn]) => {
                                const connProgress = conn.status === 'completed' ? 100 : (conn.downloadProgress || 0);
                                const activeConnectionsCount = Object.values(debugMetrics.connections).filter(c => 
                                    c.status === 'downloading' || c.status === 'completed'
                                ).length;
                                const weightPerConnection = 100 / activeConnectionsCount;
                                const contribution = (connProgress * weightPerConnection) / 100;
                                
                                return (
                                    <div key={id} className="col-md-6 mb-1">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="text-truncate me-2" title={conn.name || id}>
                                                {conn.name || id}
                                            </span>
                                            <span className="badge bg-light text-dark">
                                                {conn.status === 'completed' ? 
                                                    `✓ (+${weightPerConnection.toFixed(0)}%)` : 
                                                    `${connProgress}% (+${contribution.toFixed(1)}%)`
                                                }
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            )}

            {/* ✅ INFORMACIÓN ADICIONAL DURANTE PROCESAMIENTO */}
            {!isInitializing && isProcessing && (
                <div className="mt-2 p-2 bg-light rounded-2">
                    <div className="row small text-center">
                        <div className="col-md-6">
                            <strong>Procesando:</strong> {getCurrentProcessingComponent(loadingComponents)}
                        </div>
                        {debugMetrics.searchStartTime && totalProgress < 90 && (
                            <div className="col-md-6">
                                <strong>Tiempo estimado:</strong> {calculateETA(totalProgress, debugMetrics.searchStartTime)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ✅ MENSAJE DE FINALIZACIÓN */}
            {isCompleted && (
                <div className="mt-2 p-2 bg-success bg-opacity-10 rounded-2 text-center">
                    <div className="text-success">
                        <i className="fas fa-check-circle me-2"></i>
                        <strong>¡Reporte generado exitosamente!</strong>
                        {totalRecordsReceived > 0 && (
                            <span className="ms-2">({totalRecordsReceived.toLocaleString()} registros procesados)</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgressBarWithRealTimeMetrics;

// import React, { useState, useEffect } from 'react';
// import { ProgressBar } from 'react-bootstrap';

// /**
//  * Funciones auxiliares para cálculos de tiempo
//  */
// export const formatElapsedTime = (startTime) => {
//     if (!startTime) return "0s";
    
//     const elapsed = Date.now() - startTime;
//     if (elapsed < 60000) return `${Math.round(elapsed/1000)}s`;
//     const minutes = Math.floor(elapsed/60000);
//     const seconds = Math.round((elapsed%60000)/1000);
//     return `${minutes}m ${seconds}s`;
// };

// export const calculateETA = (currentProgress, startTime) => {
//     if (!startTime || currentProgress <= 0) return "Calculando...";
    
//     const elapsed = Date.now() - startTime;
//     const rate = currentProgress / elapsed;
//     const remaining = (100 - currentProgress) / rate;
    
//     if (remaining < 60000) return `${Math.round(remaining/1000)}s restantes`;
//     const minutes = Math.floor(remaining/60000);
//     return `${minutes}m restantes`;
// };

// export const getCurrentProcessingComponent = (loadingComponents) => {
//     const processing = Object.entries(loadingComponents)
//         .find(([_, state]) => state.loading && !state.complete);
    
//     if (!processing) return "Finalizando...";
    
//     const componentNames = {
//         table: "Tabla de datos",
//         vehicles: "Gráfico de vehículos", 
//         alerts: "Gráfico de alertas",
//         map: "Mapa",
//         frequency: "Gráfico de frecuencia"
//     };
    
//     return componentNames[processing[0]] || processing[0];
// };

// /**
//  * Componente de barra de progreso con métricas en tiempo real
//  */
// const ProgressBarWithRealTimeMetrics = ({ 
//     loadingComponents, 
//     calculateTotalProgress, 
//     debugMetrics 
// }) => {
//     const [animatedProgress, setAnimatedProgress] = useState(0);
    
//     // ✅ DETECTAR REINICIO
//     const isInitializing = !debugMetrics.searchStartTime && debugMetrics.globalTotalRecordsExpected === 0;
    
//     useEffect(() => {
//         const currentProgress = calculateTotalProgress();
//         if (Math.abs(currentProgress - animatedProgress) > 0.1) {
//             setAnimatedProgress(currentProgress);
//         }
//     }, [calculateTotalProgress, animatedProgress]);

//     // ✅ CALCULAR MÉTRICAS RELEVANTES
//     const totalRecordsReceived = debugMetrics.globalTotalRecordsReceived || 0;
//     const totalRecordsExpected = debugMetrics.globalTotalRecordsExpected || 0;
//     const downloadProgress = debugMetrics.globalDownloadProgress || 0;
//     const activeConnections = debugMetrics.activeConnections || 0;
    
//     // ✅ DETERMINAR ESTADO PRINCIPAL
//     const isDownloading = activeConnections > 0 && downloadProgress < 100;
//     const isProcessing = !isDownloading && animatedProgress > 0 && animatedProgress < 100;
//     const isCompleted = downloadProgress >= 100 && animatedProgress >= 100;

//     // ✅ DETERMINAR MENSAJE PRINCIPAL SIMPLE
//     const getMainStatus = () => {
//         if (isInitializing) {
//             return {
//                 message: "Preparando reporte...",
//                 icon: "fas fa-sync-alt fa-spin",
//                 color: "text-muted"
//             };
//         }
        
//         if (isDownloading) {
//             return {
//                 message: `Descargando datos... ${downloadProgress.toFixed(0)}%`,
//                 icon: "fas fa-download",
//                 color: "text-primary"
//             };
//         }
        
//         if (isProcessing) {
//             return {
//                 message: `Procesando gráficos... ${animatedProgress.toFixed(0)}%`,
//                 icon: "fas fa-chart-bar",
//                 color: "text-info"
//             };
//         }
        
//         if (isCompleted) {
//             return {
//                 message: "Reporte completado",
//                 icon: "fas fa-check-circle",
//                 color: "text-success"
//             };
//         }
        
//         return {
//             message: "Generando reporte...",
//             icon: "fas fa-cog fa-spin",
//             color: "text-primary"
//         };
//     };

//     const status = getMainStatus();
//     const totalProgress = isDownloading ? downloadProgress : animatedProgress;

//     return (
//         <div className="progress-container mb-3">
//             {/* ✅ ENCABEZADO SIMPLIFICADO */}
//             <div className="d-flex justify-content-between align-items-center mb-2">
//                 <div className="text-end">
//                     {/* ✅ TIEMPO TRANSCURRIDO SIMPLE */}
//                     {debugMetrics.searchStartTime && !isInitializing && (
//                         <div className="small text-muted">
//                             {formatElapsedTime(debugMetrics.searchStartTime)}
//                         </div>
//                     )}
//                 </div>
//             </div>

//             {/* ✅ BARRA DE PROGRESO PRINCIPAL */}
//             <ProgressBar 
//                 style={{ height: 20 }} 
//                 className="shadow-sm rounded-3"
//                 animated={totalProgress < 100 && !isInitializing}
//             >
//                 <ProgressBar
//                     now={isInitializing ? 0 : totalProgress}
//                     variant={
//                         isInitializing ? "secondary" :
//                         isDownloading ? "primary" :
//                         isProcessing ? "info" :
//                         isCompleted ? "success" : "primary"
//                     }
//                     style={{ 
//                         transition: 'width 0.5s ease-in-out',
//                         fontSize: '14px',
//                         fontWeight: 'bold'
//                     }}
//                     label={isInitializing ? "" : `${totalProgress.toFixed(0)}%`}
//                 />
//             </ProgressBar>

//             {/* ✅ INFORMACIÓN ADICIONAL SOLO SI ES RELEVANTE */}
//             {!isInitializing && (isDownloading || isProcessing) && (
//                 <div className="mt-2 p-2 bg-light rounded-2">
//                     <div className="row small text-center">
//                         {/* ✅ ESTADO DE CONEXIÓN */}
//                         {isDownloading && (
//                             <div className="col-md-4">
//                                 <strong>Estado:</strong> 
//                                 <span className={`ms-1 ${activeConnections > 0 ? 'text-success' : 'text-danger'}`}>
//                                     {activeConnections > 0 ? 'Conectado' : 'Desconectado'}
//                                 </span>
//                             </div>
//                         )}
                        
//                         {/* ✅ TIEMPO ESTIMADO SOLO SI ES ÚTIL */}
//                         {isDownloading && debugMetrics.searchStartTime && totalProgress < 90 && (
//                             <div className="col-md-4">
//                                 <strong>Tiempo estimado:</strong> {calculateETA(totalProgress, debugMetrics.searchStartTime)}
//                             </div>
//                         )}
                        
//                         {/* ✅ COMPONENTES PROCESÁNDOSE */}
//                         {isProcessing && (
//                             <div className="col-md-12">
//                                 <strong>Procesando:</strong> {getCurrentProcessingComponent(loadingComponents)}
//                             </div>
//                         )}
//                     </div>
//                 </div>
//             )}

//             {/* ✅ MENSAJE DE FINALIZACIÓN */}
//             {isCompleted && (
//                 <div className="mt-2 p-2 bg-success bg-opacity-10 rounded-2 text-center">
//                     <div className="text-success">
//                         <i className="fas fa-check-circle me-2"></i>
//                         <strong>¡Reporte generado exitosamente!</strong>
//                         {totalRecordsReceived > 0 && (
//                             <span className="ms-2">({totalRecordsReceived.toLocaleString()} registros procesados)</span>
//                         )}
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default ProgressBarWithRealTimeMetrics;